import { FC, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { PasswordInput } from '@/common/ui/PasswordInput'
import { Button } from '@/common/ui/button'
import { Checkbox } from '@/common/ui/checkbox'
import { InputError } from '@/common/ui/InputError'
import { Logo } from '@/common/assets/svg'
import { H3 } from '@/common/ui'
import SolidBackgroundModal from '@/common/ui/SolidBackgroundModal'
import { ONBOARDING_COPY } from '@/locales/en'
import type { UsagePolicy } from '@/common/types'
import { inviteSignupSchema, type InviteSignupFormValues } from '../rules/inviteSignupSchema'

export type PageProps = {
    email?: string
    signupTerms?: UsagePolicy
    loading?: boolean
    saving?: boolean
    disabled?: boolean
}

interface ViewModelProps extends PageProps {
    /** Form submission handler — *required for production use*; may be omitted in Storybook previews where only form state is displayed. */
    onSubmit?: (data: InviteSignupFormValues) => void
    /** Terms checkbox change handler */
    onTermsChange?: (checked: boolean) => void
}

export const InviteSignupForm: FC<ViewModelProps> = ({
    email,
    signupTerms,
    loading,
    saving,
    disabled,
    onSubmit,
    onTermsChange,
}) => {
    const [isTncAccepted, setIsTncAccepted] = useState(false)

    const {
        register,
        handleSubmit,
        control,
        setValue,
        watch,
        formState: { errors },
    } = useForm<InviteSignupFormValues>({
        resolver: yupResolver(inviteSignupSchema),
        mode: 'onBlur',
    })

    // Watch password fields to enable/disable button
    const password = watch('password')
    const confirmPassword = watch('confirmPassword')

    // Pre-populate email from prop
    useEffect(() => {
        if (email) {
            setValue('email', email)
        }
    }, [email, setValue])

    const hasPasswordValues = Boolean(password && confirmPassword)
    const isNextButtonDisabled = disabled ?? (loading || saving || !isTncAccepted || !hasPasswordValues)

    return (
        <SolidBackgroundModal>
            <div className="flex flex-col gap-12">
                <div className="flex flex-col gap-6">
                    <Logo className="h-8 mx-auto" />
                    <H3 className="text-center text-3xl text-brand-forest">Set password</H3>
                    <p className="text-sm text-center text-brand-forest">
                        Please note, SIX is currently only offered to AU residents.
                    </p>

                    <form
                        noValidate
                        className="flex flex-col gap-6"
                        onSubmit={onSubmit ? handleSubmit(onSubmit) : () => {}}
                    >
                        {/* Terms and Conditions checkbox */}
                        <div>
                            <div className="flex gap-3">
                                <Controller
                                    name="tnc"
                                    control={control}
                                    defaultValue={false}
                                    render={({ field }) => (
                                        <Checkbox
                                            id="tnc"
                                            checked={field.value}
                                            onCheckedChange={(checked) => {
                                                const isChecked = checked === true
                                                field.onChange(isChecked)
                                                setIsTncAccepted(isChecked)
                                                onTermsChange?.(isChecked)
                                            }}
                                        />
                                    )}
                                />
                                <label className="text-sm" htmlFor="tnc">
                                    {signupTerms?.disclaimer && (
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: signupTerms.disclaimer.replace(
                                                    'Privacy Policy',
                                                    'Privacy&nbsp;Policy',
                                                ),
                                            }}
                                        />
                                    )}
                                </label>
                            </div>
                            <InputError message={errors.tnc?.message} />
                        </div>

                        {/* Email - read-only display */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-slate-900">Email</label>
                            <div className="text-base text-content-primary">{email}</div>
                        </div>

                        {/* Password field */}
                        <PasswordInput
                            {...register('password')}
                            error={errors.password?.message}
                            required
                            id="password"
                            label={ONBOARDING_COPY.login.password}
                        />

                        {/* Confirm password field */}
                        <PasswordInput
                            {...register('confirmPassword')}
                            error={errors.confirmPassword?.message}
                            required
                            id="confirm-password"
                            label={ONBOARDING_COPY.login.confirmPassword}
                        />
                    </form>
                </div>

                {/* Next button - right-aligned */}
                <div className="flex justify-end">
                    <Button
                        loading={saving}
                        variant="secondary"
                        type="submit"
                        className="w-28"
                        disabled={isNextButtonDisabled}
                        onClick={onSubmit ? handleSubmit(onSubmit) : () => {}}
                    >
                        Next
                    </Button>
                </div>
            </div>
        </SolidBackgroundModal>
    )
}
